import { DashboardAction } from "../types/api.types";
import { OPERATIONS_URL } from '../config/urls';

export const dashboardActions: DashboardAction[] = [
  // OPERATIONS LOGIN 
  {
    id: "operations-login",
    title: "Login to Operations",
    description: "Access operations dashboard with current session token",
    icon: "IconLogin",
    url: "operations-login", // Special handling
    category: "user",
    priority: 0, // Separate UI, not in normal grid
    requiresToken: true,
  },

  
  
  //  Verify Order
  {
    id: "verify-order",
    title: "Verify Order",
    description: "Direct order verification - phone number pre-filled from user context",
    icon: "IconShieldCheck",
    url: "/verify-order",
    category: "rent_orders", // Using existing category to avoid red underlines
    priority: 1,
    requiresPhoneNumber: true,
  },

  // Login as User
  {
    id: "login-as-user",
    title: "Login as User", 
    description: "Login to the system as this specific user",
    icon: "IconLogin",
    url: "/login-as-user",
    category: "user",
    priority: 2,
    requiresPhoneNumber: true,
  },

  // Update Address for Orders
  {
    id: "update-address-for-orders",
    title: "Update Address for Orders",
    description: "Select an order and update its delivery address",
    icon: "IconMapPin",
    url: "/update-address",
    category: "rent_orders", // Using existing category
    priority: 3,
    requiresOrderSelection: true,
  },

  //  Update Order
  {
    id: "update-order",
    title: "Update Order",
    description: "Select an order and update its details",
    icon: "IconEdit",
    url: "/update-order",
    category: "rent_orders", // Using existing category 
    priority: 4,
    requiresOrderSelection: true,
  },

  //USER ACTIONS CATEGORY
  {
    id: "wallet-credit",
    title: "Wallet Credit",
    description: "Add credits to user's wallet",
    icon: "IconWallet",
    url: "/wallet-credit",
    category: "user",
    priority: 3,
    requiresPhoneNumber: true,
  },

  //  ORDER ACTIONS 
  {
    id: "update-order-without-change", 
    title: "Update Order (No Change)",
    description: "Update order without changing price - direct access",
    icon: "IconEdit",
    url: "/update-order-without-change",
    category: "rent_orders",
    priority: 5,
    requiresOrderId: true,
  },
  {
    id: "cancel-order",
    title: "Cancel Order", 
    description: "Select an order to cancel",
    icon: "IconX",
    url: "/cancel-order",
    category: "rent_orders",
    priority: 6,
    requiresOrderSelection: true,
  },
  {
    id: "process-refund",
    title: "Process Refunds",
    description: "Select an order to process refund",
    icon: "IconCurrencyRupee",
    url: "/process-refunds",
    category: "rent_orders", 
    priority: 7,
    requiresOrderSelection: true,
  },
  {
    id: "new-process-refund",
    title: "New Process Refunds",
    description: "Enhanced refund processing with additional options",
    icon: "IconCurrencyRupee", 
    url: "/new-process-refunds",
    category: "rent_orders",
    priority: 8,
    requiresOrderSelection: true,
  },

  
  {
    id: "customer-tickets",
    title: "Customer Ticket",
    description: "View customer ticket details - direct access",
    icon: "IconTicket",
    url: "/customer-tickets",
    category: "verification",
    priority: 1,
    requiresPhoneNumber: true,
  },
  {
    id: "support-tickets", 
    title: "Support Ticket",
    description: "Access support ticket management - direct access",
    icon: "IconTicket",
    url: "/support-tickets", 
    category: "verification",
    priority: 2,
    requiresPhoneNumber: true, 
  },
  {
    id: "rent-estimate",
    title: "Create Rent Estimate",
    description: "Create rental estimate for this user",
    icon: "IconCalculator", 
    url: "/rent-estimate",
    category: "verification",
    priority: 3,
    requiresPhoneNumber: true,
  },
  {
    id: "buy-estimate",
    title: "Create Buy Estimate", 
    description: "Create purchase estimate for this user",
    icon: "IconShoppingCart",
    url: "/buy-estimate",
    category: "verification",
    priority: 4, 
    requiresPhoneNumber: true,
  },
  {
    id: "send-customer-prompt",
    title: "Send Customer Notification",
    description: "Send notifications or prompts to this customer",
    icon: "IconBell",
    url: "/send-customer-prompt", 
    category: "verification",
    priority: 5,
    requiresPhoneNumber: true,
  },
  {
    id: "defaulters",
    title: "Check Defaulter Status",
    description: "Check if this user is in defaulters list", 
    icon: "IconAlertTriangle",
    url: "/defaulters",
    category: "verification",
    priority: 6,
    requiresUserId: true,
  },
];

export const getActionsByCategory = (category: DashboardAction['category']) => {
  return dashboardActions
    .filter(action => action.category === category && action.id !== 'operations-login')
    .sort((a, b) => a.priority - b.priority);
};

export const getAllActionsSorted = () => {
  return dashboardActions
    .filter(action => action.id !== 'operations-login')
    .sort((a, b) => a.priority - b.priority);
};

export const getOperationsLoginAction = () => {
  return dashboardActions.find(action => action.id === 'operations-login');
};

export const buildActionUrl = (action: DashboardAction, params: {
  userId?: number;
  orderId?: string;
  phoneNumber?: string;
  countryCode?: string;
  ticketId?: string;
  ticketType?: string;
}) => {

  // Special handling for operations login
  if (action.id === 'operations-login') {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token') || '';
    const operationsUrl = OPERATIONS_URL || 'https://operations.sharepal.co.in';
    const finalUrl = `${operationsUrl}/?token=${token}`;
    return finalUrl;
  }

  const url = new URL(action.url, window.location.origin);

  if (action.requiresUserId && params.userId) {
    url.searchParams.set('user_id', params.userId.toString());
  }

  if (action.requiresOrderSelection && params.orderId) {
    url.searchParams.set('order_id', params.orderId);
  } else if (action.requiresOrderId && params.orderId) {
    url.searchParams.set('order_id', params.orderId);
  }

  if (action.requiresPhoneNumber && params.phoneNumber && params.countryCode) {
    url.searchParams.set('calling_number', params.phoneNumber);
    const cleanCountryCode = params.countryCode.replace('+', '');
    url.searchParams.set('country_code_calling', cleanCountryCode);
  }

  console.log(' DEBUG: Final URL:', url.pathname + url.search);
  return url.pathname + url.search;
};
